<template>
    <div class="live-toast-stack">
        <transition-group name="live-toast" tag="div">
            <div v-for="toast in toasts" :key="toast.id"
                 class="live-toast animated bounceInRight"
                 @click="open(toast)">
                <div class="live-toast-accent"></div>
                <div class="live-toast-body">
                    <button type="button" class="live-toast-close"
                            @click.stop="dismiss(toast.id)" aria-label="Dismiss">&times;</button>
                    <div class="live-toast-head">
                        <i class="fa fa-bolt"></i> New order received
                    </div>
                    <h3 class="live-toast-order">#{{ toast.orderId }}</h3>
                    <div class="live-toast-meta">
                        <div><i class="fa fa-building-o"></i> {{ toast.restaurantName || 'Unknown restaurant' }}</div>
                        <div><i class="fa fa-map-marker"></i> {{ toast.branchArea || 'Unknown area' }}</div>
                    </div>
                    <div class="live-toast-cta">Click to open Live Orders <i class="fa fa-angle-right"></i></div>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<script>
    /* eslint-disable */

    /**
     * The portal-wide new-order popup, top right, on any page.
     *
     * A Vue component rather than the global toastr used by the branch flow, for two
     * reasons: it can route with $router.push instead of reassigning window.location to a
     * hash URL (which reloads the app and throws away Vuex), and it avoids the branch
     * path's habit of re-binding a jQuery click handler to every toast still on screen
     * each time a new one arrives.
     *
     * The module decides whether a toast is pushed at all — nothing is queued while the
     * operator is already watching the live page.
     */
    const AUTO_DISMISS_MS = 12000

    export default {
        name: "LiveOrderToast",
        computed: {
            toasts() {
                return this.$store.state.liveOrders.toasts;
            }
        },
        watch: {
            toasts(list) {
                list.forEach(toast => {
                    if (this.timers[toast.id]) return;
                    this.timers[toast.id] = setTimeout(() => this.dismiss(toast.id), AUTO_DISMISS_MS);
                });
            }
        },
        data() {
            return {
                timers: {}
            }
        },
        methods: {
            dismiss(id) {
                if (this.timers[id]) {
                    clearTimeout(this.timers[id]);
                    delete this.timers[id];
                }
                this.$store.commit('liveOrders/dismissToast', id);
            },
            open(toast) {
                this.$store.commit('liveOrders/setFocusOrderId', toast.orderId);
                this.dismiss(toast.id);
                if (this.$route.path !== '/live_orders') {
                    this.$router.push('/live_orders');
                }
            }
        },
        beforeDestroy() {
            Object.keys(this.timers).forEach(id => clearTimeout(this.timers[id]));
            this.timers = {};
        }
    }
</script>

<style scoped>
    /* .live-toast-stack carries no positioning on purpose: .portal-toast-column in App.vue
       places it, shared with the menu alert toast so the two never overlap. */

    .live-toast {
        position: relative;
        display: flex;
        margin-bottom: 10px;
        background: #ffffff;
        border: 1px solid #e7eaec;
        border-radius: 3px;
        box-shadow: 0 4px 18px rgba(0, 0, 0, .18);
        cursor: pointer;
        overflow: hidden;
        animation-duration: .5s;
    }

    .live-toast:hover {
        box-shadow: 0 6px 22px rgba(0, 0, 0, .26);
    }

    /* Same amber as the UN-ASSIGNED status label, so the popup reads as that status. */
    .live-toast-accent {
        width: 5px;
        flex: 0 0 5px;
        background: #f8ac59;
    }

    .live-toast-body {
        padding: 12px 14px 12px 12px;
        flex: 1 1 auto;
    }

    .live-toast-close {
        position: absolute;
        top: 4px;
        right: 8px;
        border: 0;
        background: none;
        font-size: 18px;
        line-height: 1;
        color: #b0b4b6;
        padding: 2px 4px;
    }

    .live-toast-close:hover { color: #676a6c; }

    .live-toast-head {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .4px;
        text-transform: uppercase;
        color: #f8ac59;
        margin-bottom: 2px;
    }

    .live-toast-order {
        margin: 0 0 6px;
        font-size: 22px;
        font-weight: 600;
        color: #676a6c;
    }

    .live-toast-meta {
        font-size: 12px;
        color: #676a6c;
        line-height: 1.7;
    }

    .live-toast-meta i {
        width: 14px;
        color: #9a9da0;
    }

    .live-toast-cta {
        margin-top: 8px;
        padding-top: 7px;
        border-top: 1px solid #f1f3f4;
        font-size: 11px;
        color: #1ab394;
        font-weight: 600;
    }

    .live-toast-leave-active {
        transition: opacity .3s ease, transform .3s ease;
    }

    .live-toast-leave-to {
        opacity: 0;
        transform: translateX(30px);
    }
</style>
